import { createContext, useState, useContext } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type { userContextQuery } from './__generated__/userContextQuery.graphql';
import type { UserConfigurationState } from './state';
import { loadUserConfigurationLocal, saveUserConfigurationLocal } from './storage';
import { useKindeUser } from '@vitvio/helpers';

const userQuery = graphql`
  query userContextQuery {
    userOrganization {
      id
      name
      hospitals(first: 10) {
        edges {
          node {
            id
            name
            timezone
          }
        }
      }
    }
  }
`;

// TODO: use the generated types
interface AvailableHospitals {
  readonly id: string | undefined | null;
  readonly name: string | undefined | null;
  readonly timezone: string | undefined | null;
}

interface UserContextType {
  userID: string | undefined | null;
  username: string;
  activeHospital: {
    id: string;
    workdayStartHour: number;
    workdayStopHour: number;
  };
  organization: userContextQuery['response']['userOrganization'];
  changeActiveHospital: (newHospital: UserConfigurationState['selectedHospitalID']) => void;
  availableHospitals: (AvailableHospitals | undefined | null)[] | undefined | null;
  hospitalTimezone: string;
  timeFormat: '12hr' | '24hr';
  updateTimeFormat: (format: '12hr' | '24hr') => void;
  refreshUserContext: () => void;
}

const UserContext = createContext<UserContextType | null>(null);

interface UserProviderProps {
  children: React.ReactNode;
  loaderComponent: React.ReactNode;
}

function UserProvider({ children, loaderComponent }: UserProviderProps) {
  const user = useKindeUser();
  const [fetchKey, setFetchKey] = useState(0);
  const orgData = useLazyLoadQuery<userContextQuery>(userQuery, {}, { fetchPolicy: 'network-only', fetchKey });
  const [userConfiguration, setUserConfiguration] = useState<UserConfigurationState>(loadUserState());
  const [timeFormat, setTimeformat] = useState<'12hr' | '24hr'>('24hr');

  function updateUserConfiguration(newConfig: UserConfigurationState) {
    setUserConfiguration(newConfig);
    saveUserConfigurationLocal(newConfig);
  }

  function loadUserState() {
    const config = loadUserConfigurationLocal(orgData.userOrganization?.id || '', user?.id || '');

    if (!config.organizationID) {
      config.organizationID = orgData?.userOrganization?.id || '';
      config.userID = user?.id || '';
    }

    if (!config.selectedHospitalID) {
      config.selectedHospitalID = orgData?.userOrganization?.hospitals?.edges?.[0]?.node?.id || '';
      saveUserConfigurationLocal(config);
    }

    return config;
  }

  function changeActiveHospital(hospital: UserConfigurationState['selectedHospitalID']) {
    updateUserConfiguration({
      ...userConfiguration,
      selectedHospitalID: hospital,
    });
  }

  function updateTimeFormat(format: '12hr' | '24hr') {
    updateUserConfiguration({
      ...userConfiguration,
      timeFormat: format,
    });
    setTimeformat(format);
  }

  function refreshUserContext() {
    setFetchKey(fetchKey + 1);
  }

  function getActiveHospital() {
    return orgData.userOrganization?.hospitals?.edges?.find(
      (edge) => edge?.node?.id === userConfiguration.selectedHospitalID,
    );
  }

  if (!orgData) {
    return loaderComponent;
  }

  // Select first hospital if no hospital is selected or if the selected hospital no longer exists
  const selectedHospitalExists = orgData.userOrganization?.hospitals?.edges?.some(
    (edge) => edge?.node?.id === userConfiguration.selectedHospitalID,
  );

  if (
    orgData.userOrganization?.hospitals?.edges?.length &&
    (!userConfiguration.selectedHospitalID || !selectedHospitalExists)
  ) {
    changeActiveHospital(orgData.userOrganization?.hospitals?.edges[0]?.node?.id || '');
  }

  return (
    <UserContext.Provider
      value={{
        userID: user?.id,
        username: `${user?.given_name} ${user?.family_name}`,
        activeHospital: {
          id: userConfiguration?.selectedHospitalID,
          workdayStartHour: userConfiguration.workday.start,
          workdayStopHour: userConfiguration.workday.stop,
        },
        organization: orgData.userOrganization,
        changeActiveHospital,
        availableHospitals: orgData.userOrganization?.hospitals?.edges?.map((edge) => edge?.node),
        hospitalTimezone: getActiveHospital()?.node?.timezone || '',
        timeFormat,
        updateTimeFormat,
        refreshUserContext,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

function useUserContext() {
  const context = useContext(UserContext);
  if (!context) throw new Error('User Context was used outside the user provider.');
  return context;
}

export { UserProvider, useUserContext };
